import { useEffect } from "react";
import { Outlet } from "react-router";

import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  VisuallyHidden,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useBreakpoint } from "@reactive-resume/hooks";

import { useBuilderStore } from "@/client/stores/builder";
import { NotificationBanner } from "@/client/components/NotificationBanner";

import { LeftSidebar } from "./sidebars/left";
import { BuilderHeader } from "./_components/header";
import { BuilderToolbar } from "./_components/toolbar";

const onOpenAutoFocus = (event: Event) => {
  event.preventDefault();
};

// Renders entire control tabs and resume screen layout */
export const BuilderLayout = () => {
  const { isDesktop } = useBreakpoint();
  const sheet = useBuilderStore((state) => state.sheet);
  const setSheet = useBuilderStore((state) => state.sheet.setOpen);

  const leftSetSize = useBuilderStore((state) => state.panel.left.setSize);
  const leftHandle = useBuilderStore((state) => state.panel.left.handle);

  // Ensure one sidebar is always visible on desktop
  useEffect(() => {
    if (!isDesktop) return;

    if (!sheet.left.open && !sheet.right.open) {
      setSheet("left", true);
    }
  }, [isDesktop, sheet.left.open, sheet.right.open, setSheet]);

  if (isDesktop) {
    return (
      <div className="relative h-screen min-h-0 w-full overflow-hidden">
        <NotificationBanner />
        <PanelGroup direction="horizontal">
          <Panel
            minSize={sheet.left.open ? 30 : 0}
            maxSize={sheet.left.open ? 45 : 0}
            defaultSize={sheet.left.open ? 40 : 0}
            className={cn(
              "z-10 shadow-xl",
              !leftHandle.isDragging && "transition-[flex]",
              !sheet.left.open && "!flex-0",
            )}
            onResize={leftSetSize}
          >
            <div className={cn("h-full", !sheet.left.open && "hidden")}>
              <LeftSidebar />
            </div>
          </Panel>
          <PanelResizeHandle
            isDragging={leftHandle.isDragging}
            onDragging={leftHandle.setDragging}
            className={cn(!sheet.left.open && "hidden")}
          />
          <Panel>
            <div className="flex h-full min-h-0 w-full flex-col overflow-hidden px-4">
              <BuilderHeader />
              <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                <Outlet />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center pb-6">
                  <div className="pointer-events-auto">
                    <BuilderToolbar />
                  </div>
                </div>
              </div>
            </div>
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  // Renders entire layout for small screen size
  return (
    <div className="relative h-screen min-h-0 w-full overflow-hidden">
      <Sheet open={sheet.left.open} onOpenChange={sheet.left.setOpen}>
        <VisuallyHidden>
          <SheetHeader>
            <SheetTitle />
            <SheetDescription />
          </SheetHeader>
        </VisuallyHidden>

        <SheetContent
          side="left"
          showClose={false}
          className="top-16 w-full bg-secondary-accent/30 p-0 sm:max-w-xl"
          onOpenAutoFocus={onOpenAutoFocus}
        >
          <LeftSidebar />
        </SheetContent>
      </Sheet>
    </div>
  );
};
